import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { Shield, GitBranch, Music } from "lucide-react";
const About: React.FC = () => {
  return <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-tagblue via-tagblue-light to-tagblue text-white overflow-hidden">
        {/* Subtle 3D Background Elements */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-32 left-10 w-20 h-20 bg-white/10 rounded-2xl backdrop-blur-sm rotate-12 animate-float" />
          <div className="absolute top-20 right-20 w-16 h-16 bg-white/10 rounded-xl backdrop-blur-sm -rotate-12 animate-float" style={{animationDelay: '2s'}} />
          <div className="absolute bottom-32 left-20 w-12 h-12 bg-white/10 rounded-lg backdrop-blur-sm rotate-45 animate-float" style={{animationDelay: '4s'}} />
          <div className="absolute bottom-20 right-10 w-16 h-16 bg-white/10 rounded-xl backdrop-blur-sm -rotate-6 animate-float" style={{animationDelay: '1s'}} />
        </div>
        
        <div className="relative z-50">
          <Navbar />
        </div>
        <div className="container mx-auto px-4 py-16 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 animate-slide-up">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-blue-100 drop-shadow-2xl">
              About Tagmusic
            </span>
          </h1>
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-4 border border-white/20 shadow-2xl animate-slide-up max-w-2xl mx-auto" style={{animationDelay: '0.2s'}}>
            <p className="text-white/95 text-lg leading-relaxed">
              Pioneering Ethical AI in Music
            </p>
          </div>
        </div>
      </div>
      
      {/* Our Vision */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-100 relative overflow-hidden">
        {/* Enhanced 3D Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-24 h-24 bg-tagblue/10 rounded-full blur-xl animate-float" />
          <div className="absolute bottom-10 left-10 w-32 h-32 bg-tagblue-light/10 rounded-full blur-xl animate-float" style={{animationDelay: '3s'}} />
          <div className="absolute top-32 left-20 w-20 h-20 bg-tagblue/15 rounded-3xl backdrop-blur-sm rotate-12 animate-float" style={{animationDelay: '1s'}} />
          <div className="absolute bottom-32 right-20 w-16 h-16 bg-tagblue-light/15 rounded-2xl backdrop-blur-sm -rotate-12 animate-float" style={{animationDelay: '2s'}} />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="group relative">
              {/* Card glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-3xl blur-2xl opacity-25 group-hover:opacity-50 transition-opacity duration-500" />
              
              {/* Main card */}
              <div className="relative bg-white/80 backdrop-blur-md rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/50 group-hover:scale-105 transform">
                <img src="/lovable-uploads/5a8c5d4b-4175-4762-bcaf-f8e00f4bfdb6.png" alt="AI Music Classification Diagram" className="w-full h-auto object-cover rounded-2xl" style={{
                filter: "drop-shadow(0 0 1px rgba(0, 0, 0, 0.2)) contrast(1.02)",
                mixBlendMode: "multiply"
              }} />
              </div>
            </div>
            
            <div className="group relative">
              {/* Card glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-tagblue-light to-tagblue rounded-3xl blur-2xl opacity-25 group-hover:opacity-50 transition-opacity duration-500" />
              
              {/* Main card */}
              <div className="relative bg-white/80 backdrop-blur-md rounded-3xl p-10 shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/50">
                <h2 className="text-4xl font-bold mb-6">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-tagblue to-gray-900">
                    Our Vision
                  </span>
                </h2>
                <p className="text-gray-600 mb-4 leading-relaxed">Tagmusic, by Vlinder Inc. is the Music-Tech subsidiary, created with the vision to simplify AI inclusion in Music Industry.  It aims to solve all IP related issues that comes with Generative AI in Music Industry.</p>
                <p className="text-gray-600 mb-4 leading-relaxed">Tagmusic is compliant with C2PA, DDEX, W3C to enable accurate 'source' classification of music files in 3 categories following the principles laid by industry leaders - AI generated, AI assisted &amp; Human Generated Music.</p>
                <p className="text-gray-600 leading-relaxed">We aim to help labels, PROs, DSPs, platforms &amp; other stakeholders to maintain AI transparency in the age of AI-generated content. To prevent identity theft, mass infringements &amp; IP scraping threatening the integrity of Music Industry, we aim to implement Ethical AI principles with provenance and detection tools.</p>
                
                {/* Decorative bottom accent */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-full opacity-60" />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Values */}
      <section className="py-16 bg-gray-800 text-white relative overflow-hidden">
        {/* 3D Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-24 h-24 bg-tagblue/20 rounded-3xl backdrop-blur-sm rotate-12 animate-float" />
          <div className="absolute top-20 right-20 w-16 h-16 bg-tagblue-light/20 rounded-xl backdrop-blur-sm -rotate-12 animate-float" style={{animationDelay: '2s'}} />
          <div className="absolute bottom-20 left-20 w-12 h-12 bg-tagblue/20 rounded-lg backdrop-blur-sm rotate-45 animate-float" style={{animationDelay: '4s'}} />
          <div className="absolute bottom-10 right-10 w-20 h-20 bg-tagblue-light/20 rounded-xl backdrop-blur-sm -rotate-6 animate-float" style={{animationDelay: '1s'}} />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-4xl font-bold text-center mb-12">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-tagblue to-white">
              Our Values
            </span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Value 1 */}
            <div className="group relative">
              {/* Card glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-3xl blur-2xl opacity-25 group-hover:opacity-50 transition-opacity duration-500" />
              
              {/* Main card */}
              <div className="relative bg-white/5 backdrop-blur-md p-8 rounded-3xl text-center border border-white/10 shadow-xl hover:shadow-2xl transition-all duration-500 group-hover:scale-105 transform">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-tagblue to-tagblue-dark rounded-xl flex items-center justify-center shadow-lg">
                    <Shield className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-4 text-white">Transparency</h3>
                <p className="text-gray-300 leading-relaxed">
                  We believe in creating a transparent music ecosystem where the origin 
                  of every piece is clear and verifiable.
                </p>
                
                {/* Decorative bottom accent */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-full opacity-60" />
              </div>
            </div>
            
            {/* Value 2 */}
            <div className="group relative" style={{animationDelay: '0.2s'}}>
              {/* Card glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-tagblue-light to-tagblue rounded-3xl blur-2xl opacity-25 group-hover:opacity-50 transition-opacity duration-500" />
              
              {/* Main card */}
              <div className="relative bg-white/5 backdrop-blur-md p-8 rounded-3xl text-center border border-white/10 shadow-xl hover:shadow-2xl transition-all duration-500 group-hover:scale-105 transform">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-tagblue-light to-tagblue rounded-xl flex items-center justify-center shadow-lg">
                    <GitBranch className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-4 text-white">Innovation</h3>
                <p className="text-gray-300 leading-relaxed">
                  Pushing the boundaries of technology to serve the evolving needs of 
                  the music industry.
                </p>
                
                {/* Decorative bottom accent */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-tagblue-light to-tagblue rounded-full opacity-60" />
              </div>
            </div>
            
            {/* Value 3 */}
            <div className="group relative" style={{animationDelay: '0.4s'}}>
              {/* Card glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-3xl blur-2xl opacity-25 group-hover:opacity-50 transition-opacity duration-500" />
              
              {/* Main card */}
              <div className="relative bg-white/5 backdrop-blur-md p-8 rounded-3xl text-center border border-white/10 shadow-xl hover:shadow-2xl transition-all duration-500 group-hover:scale-105 transform">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-tagblue to-tagblue-dark rounded-xl flex items-center justify-center shadow-lg">
                    <Music className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-4 text-white">Accuracy</h3>
                <p className="text-gray-300 leading-relaxed">Delivering precise and reliable classification of music files through advanced provenance technology.</p>
                
                {/* Decorative bottom accent */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-full opacity-60" />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Team - Hidden for now but structure is in place */}
      <section className="hidden py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Our Team</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Team Member 1 */}
            <div className="text-center">
              <div className="w-40 h-40 rounded-full bg-gray-200 mx-auto mb-4"></div>
              <h3 className="font-bold">Leadership Position</h3>
              <p className="text-gray-600">Role Title</p>
              <p className="text-sm text-gray-500 mt-2">
                Biography placeholder - Leadership background and expertise details.
              </p>
            </div>
            
            {/* Team Member 2 */}
            <div className="text-center">
              <div className="w-40 h-40 rounded-full bg-gray-200 mx-auto mb-4"></div>
              <h3 className="font-bold">Leadership Position</h3>
              <p className="text-gray-600">Role Title</p>
              <p className="text-sm text-gray-500 mt-2">
                Biography placeholder - Leadership background and expertise details.
              </p>
            </div>
            
            {/* Team Member 3 */}
            <div className="text-center">
              <div className="w-40 h-40 rounded-full bg-gray-200 mx-auto mb-4"></div>
              <h3 className="font-bold">Leadership Position</h3>
              <p className="text-gray-600">Role Title</p>
              <p className="text-sm text-gray-500 mt-2">
                Biography placeholder - Leadership background and expertise details.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Standards Compliance */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        {/* 3D Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-24 h-24 bg-tagblue/10 rounded-3xl backdrop-blur-sm rotate-12 animate-float" />
          <div className="absolute bottom-20 right-20 w-20 h-20 bg-tagblue-light/10 rounded-2xl backdrop-blur-sm -rotate-12 animate-float" style={{animationDelay: '2s'}} />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-4xl font-bold text-center mb-12">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-tagblue to-gray-900">
              Standards Compliance
            </span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Standard 1 */}
            <div className="group relative">
              {/* Card glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-3xl blur-2xl opacity-25 group-hover:opacity-50 transition-opacity duration-500" />
              
              {/* Main card */}
              <div className="relative bg-white/80 backdrop-blur-md p-8 rounded-3xl text-center shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/50 group-hover:scale-105 transform">
                <h3 className="text-xl font-bold mb-4 text-tagblue">C2PA & CAWG</h3>
                <p className="text-gray-600">C2PA & CAWG Implementor</p>
                
                {/* Decorative bottom accent */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-full opacity-60" />
              </div>
            </div>
            
            {/* Standard 2 */}
            <div className="group relative" style={{animationDelay: '0.2s'}}>
              {/* Card glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-tagblue-light to-tagblue rounded-3xl blur-2xl opacity-25 group-hover:opacity-50 transition-opacity duration-500" />
              
              {/* Main card */}
              <div className="relative bg-white/80 backdrop-blur-md p-8 rounded-3xl text-center shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/50 group-hover:scale-105 transform">
                <h3 className="text-xl font-bold mb-4 text-tagblue">DDEX</h3>
                <p className="text-gray-600">DDEX Implementor</p>
                
                {/* Decorative bottom accent */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-tagblue-light to-tagblue rounded-full opacity-60" />
              </div>
            </div>
            
            {/* Standard 3 */}
            <div className="group relative" style={{animationDelay: '0.4s'}}>
              {/* Card glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-3xl blur-2xl opacity-25 group-hover:opacity-50 transition-opacity duration-500" />
              
              {/* Main card */}
              <div className="relative bg-white/80 backdrop-blur-md p-8 rounded-3xl text-center shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/50 group-hover:scale-105 transform">
                <h3 className="text-xl font-bold mb-4 text-tagblue">W3C</h3>
                <p className="text-gray-600">Certified Partner</p>
                
                {/* Decorative bottom accent */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-full opacity-60" />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Want to Learn More */}
      <div className="relative py-24 bg-gradient-to-br from-tagblue via-tagblue-dark to-tagblue text-white overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-float" />
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-white/5 rounded-full blur-2xl animate-float" style={{
          animationDelay: '2s'
        }} />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        </div>
        
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Main content */}
            <div className="mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-blue-100 to-white">
                Want to Learn More?
              </h2>
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto">
                Get in touch with us to discover how Tagmusic can help your organization navigate the future of AI in music.
              </p>
            </div>
            
            {/* CTA Button */}
            <div className="relative max-w-2xl mx-auto">
              {/* Button glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-white/30 to-blue-200/30 rounded-3xl blur-2xl opacity-60" />
              
              <div className="relative bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 shadow-2xl">
                <Link to="/contact" className="group bg-white text-tagblue hover:bg-blue-50 px-8 py-4 rounded-2xl font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 border border-white/30 inline-flex items-center gap-2">
                  Contact Us
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>;
};
export default About;